import { useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck, BellRing, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'ahora';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  return `hace ${Math.floor(hours / 24)} d`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [pushState, setPushState] = useState('unknown');
  const [enabling, setEnabling] = useState(false);
  const ref = useRef(null);

  const load = () => {
    api.get('/me/notifications').then(({ data }) => setData(data)).catch(() => {});
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => {
      clearInterval(interval);
      document.removeEventListener('mousedown', onClick);
    };
  }, []);

  useEffect(() => {
    if (!('Notification' in window)) setPushState('unsupported');
    else setPushState(Notification.permission);
  }, []);

  const markAllRead = async () => {
    await api.post('/me/notifications/read-all').catch(() => {});
    load();
  };

  const enablePush = async () => {
    setEnabling(true);
    try {
      const permission = await Notification.requestPermission();
      setPushState(permission);
      if (permission !== 'granted') {
        toast.error('Permiso de notificaciones denegado por el navegador.');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const { data: keyData } = await api.get('/push/public-key');
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(keyData.publicKey),
      });
      await api.post('/push/subscribe', sub.toJSON());
      toast.success('Notificaciones activadas en este dispositivo.');
    } catch (err) {
      toast.error('No se pudieron activar las notificaciones push.');
    } finally {
      setEnabling(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="notifications-bell-button"
        className="relative flex h-10 w-10 items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-slate-100"
        aria-label="Notificaciones"
      >
        <Bell className="h-5 w-5" />
        {data.unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white" data-testid="notifications-badge">
            {data.unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 z-50 w-80 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl" data-testid="notifications-panel">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <p className="font-heading text-sm font-bold text-slate-900">Notificaciones</p>
            {data.unread > 0 && (
              <button onClick={markAllRead} className="flex items-center gap-1 text-xs font-semibold text-cnib-blue hover:text-cnib-bluehover" data-testid="notifications-read-all">
                <CheckCheck className="h-3.5 w-3.5" /> Marcar leídas
              </button>
            )}
          </div>
          {pushState === 'default' && (
            <button
              onClick={enablePush}
              disabled={enabling}
              data-testid="push-optin-button"
              className="flex w-full items-center gap-3 border-b border-orange-100 bg-orange-50 px-4 py-3 text-left transition-colors hover:bg-orange-100"
            >
              {enabling ? <Loader2 className="h-4 w-4 animate-spin text-orange-500" /> : <BellRing className="h-4 w-4 text-orange-500" />}
              <span className="text-xs font-semibold text-orange-700">
                Activar notificaciones push en este dispositivo
              </span>
            </button>
          )}
          <div className="max-h-96 overflow-y-auto">
            {data.items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-400">Sin notificaciones por ahora.</p>
            ) : (
              data.items.map((n) => (
                <a
                  key={n.notification_id}
                  href={n.url || '#'}
                  className={`block border-b border-slate-50 px-4 py-3 transition-colors hover:bg-slate-50 ${!n.read ? 'bg-cnib-blue/5' : ''}`}
                  data-testid={`notification-item-${n.notification_id}`}
                >
                  <p className="text-sm font-bold text-slate-800">{n.title}</p>
                  <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{n.body}</p>
                  <p className="mt-1 text-[10px] text-slate-400">{timeAgo(n.created_at)}</p>
                </a>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
